import { useMemo } from "react";
import { useAppSelector } from "@/stores/hook";
import { selectDynamicFilters } from "@/features/dynamic/store/dynamicFilterSlice";
import { serializeFilterParams } from "@/features/leads/api/useLeadFilterParams";
import { useViewPinnedStageId } from "@/features/favoriteViews/utils/useViewPinnedStage";

/**
 * Combines live search with the snapshot of panel filters
 * that were committed via the "Apply filters" button for a specific dynamic route.
 *
 * On a Favorite View saved from a stage route this also re-attaches that
 * stage as `stageId`. THE ONE FUNNEL: the list, the count, the header picker
 * counts, Select All and Export all read their params from here, and the
 * server reads `?stageId=` as "this request IS that stage's list" (same chain
 * the stage route runs — see `resolveListContext`). Without it a saved view
 * hit the plain leads list: same chips, different rows, ordered and dated by
 * creation instead of by the move into the stage.
 */
export function useDynamicFilterParams(
  routeId: string | number
): Record<string, any> {
  const { search, appliedFilters } = useAppSelector((state) =>
    selectDynamicFilters(state, routeId)
  );
  const pinnedStageId = useViewPinnedStageId(routeId);

  return useMemo(() => {
    const params = serializeFilterParams(appliedFilters, search);
    if (pinnedStageId != null) params.stageId = pinnedStageId;
    return params;
  }, [search, appliedFilters, pinnedStageId]);
}
